import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { authStrategy } from '@/lib/auth/strategy';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { API_USER_APPEARANCE_PREFERENCES_ME, API_USER_LOGOUT } from '@/service/url';
import { getUser, getUserAccessToken } from '@/utilities/setting';
import socket from '@/utilities/socket';
import adminSocket from '@/utilities/adminSocket';
import type { BrandingResponse, LoginResponseRaw, UserProfile } from '@/types/api';
import type { LoginInput } from './schema';

/* --------------------------------------------------------------- *
 * Branding — public endpoint that returns company name + logo URL  *
 * for the current host.                                            *
 * --------------------------------------------------------------- */

export function useBranding() {
  return useQuery({
    queryKey: ['branding', window.location.hostname],
    queryFn: () => api.get<BrandingResponse>(`/api/obd/branding/${window.location.hostname}`),
    /* Cache aggressively — branding rarely changes. */
    staleTime: 60 * 60 * 1000,
    /* Even an empty response is fine, falls back to defaults. */
    retry: false,
  });
}

/* --------------------------------------------------------------- *
 * Login                                                            *
 * --------------------------------------------------------------- */

interface LoginSuccess {
  userId: string;
  token: string;
  role: string;
}

/**
 * Legacy java_backend contract:
 *   POST /api/obd/login   body: { username, password }
 *   200 -> { userid, token, role: string[] }   ← note lowercase `userid`
 *   401 -> { message: 'Bad credentials' }
 *
 * We normalize to camelCase internally so feature code reads `userId`.
 */
export function useLoginMutation() {
  const loginToStore = useAuthStore((s) => s.login);
  return useMutation({
    mutationFn: async (input: LoginInput): Promise<LoginSuccess> => {
      const raw = await api.post<LoginResponseRaw>('/api/obd/login', input, {
        // Force no Authorization on /login even if a stale token is around.
        transformRequest: [(data, headers) => {
          if (headers) delete (headers as Record<string, unknown>).Authorization;
          return JSON.stringify(data);
        }],
      });
      if (!raw.userid || !raw.token || !raw.role?.length) {
        throw new Error(raw.message ?? 'Login failed');
      }
      const role = raw.role[0];
      if (!role) throw new Error('Login response missing role');
      return { userId: raw.userid, token: raw.token, role };
    },
    onSuccess: (data) => {
      loginToStore({
        user: { userId: data.userId, role: data.role },
        token: data.token,
      });
    },
  });
}

/* --------------------------------------------------------------- *
 * User profile — runs after login to populate username/balance/etc *
 * --------------------------------------------------------------- */

export function useUserProfile() {
  const userId = useAuthStore((s) => s.userId);
  const hydrateProfile = useAuthStore((s) => s.hydrateProfile);
  return useQuery({
    queryKey: ['user-profile', userId],
    queryFn: async () => {
      const profile = await api.get<UserProfile>(`/api/obd/user/profile/${userId}`);
      hydrateProfile({ username: profile.username, name: profile.name });
      return profile;
    },
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
  });
}

/* --------------------------------------------------------------- *
 * Per-user Appearance / UI theme settings                          *
 *   GET  /api/v2/user-appearance-preferences/me                    *
 *   POST /api/v2/user-appearance-preferences/me  body { json|preferences } *
 *                                                                  *
 * Persisted in user_appearance_preferences.preferences (JSONB).    *
 * AppShell hydrates Zustand from `data.json` after login and       *
 * pushes changes back (debounced) via the mutation below.          *
 *                                                                  *
 * Auth note: LoginPage uses legacy storeUser / Storage tokens, not *
 * useAuthStore.userId — gate on access token (getUserAccessToken)  *
 * so saves actually hit the API.                                   *
 * --------------------------------------------------------------- */

export interface UiSettingsResponse {
  json: string | null;
}

function hasLegacyAuthSession(): boolean {
  try {
    return Boolean(getUserAccessToken() || authStrategy.getAccessToken());
  } catch {
    return Boolean(authStrategy.getAccessToken());
  }
}

export function useUiSettings() {
  const authStoreUserId = useAuthStore((s) => s.userId);
  const enabled = Boolean(authStoreUserId) || hasLegacyAuthSession();

  return useQuery({
    queryKey: ['ui-settings', authStoreUserId || 'legacy'],
    queryFn: async (): Promise<UiSettingsResponse> => {
      const data = await getApiCall(API_USER_APPEARANCE_PREFERENCES_ME, {});
      return {
        json:
          typeof data?.json === 'string'
            ? data.json
            : data?.preferences
              ? JSON.stringify(data.preferences)
              : null,
      };
    },
    enabled,
    staleTime: Infinity,
    retry: false,
  });
}

export function useSaveUiSettings() {
  return useMutation({
    mutationFn: async (json: string) => {
      if (!hasLegacyAuthSession()) {
        throw new Error('Not authenticated');
      }
      return postApiCall(API_USER_APPEARANCE_PREFERENCES_ME, { json });
    },
  });
}

/* --------------------------------------------------------------- *
 * Logout                                                           *
 * --------------------------------------------------------------- */

/**
 * Logout is a pure client operation against java_backend (no server
 * endpoint). The future obd3-api will gain POST /api/v3/auth/logout
 * which clears the httpOnly cookie server-side — when that exists,
 * this mutation will POST first, then clear local state.
 */
/**
 * Logout. Invalidates the session on the backend BEFORE clearing local
 * state — this used to be an obd3 stub that only cleared the store, so the
 * header's Logout never told the API and the session stayed alive
 * server-side (v1 header.js:708 "Always call logout API").
 *
 * The local clear runs even if the request fails, so an expired or refused
 * token can never strand the user in a signed-in shell.
 */
export function useLogoutMutation() {
  const logout = useAuthStore((s) => s.logout);
  return useMutation({
    mutationFn: async (opts?: { onlyLogoutFromDashboard?: boolean }) => {
      const userId = getUser()?.id;
      try {
        if (userId) {
          await postApiCall(`${API_USER_LOGOUT}/${userId}`, {
            onlyLogoutFromDashboard: Boolean(opts?.onlyLogoutFromDashboard),
          });
        }
      } finally {
        // Centralized here so every logout entry point (Header.tsx,
        // cti2 Topbar.tsx, etc.) gets it for free instead of each one
        // having to remember — without this, the singleton socket stayed
        // connected past logout (SPA navigation doesn't tear it down),
        // so the backend never saw a real disconnect and kept the agent
        // "present" until the tab was actually closed.
        try { socket.disconnect(); } catch { /* noop */ }
        try { adminSocket.disconnect(); } catch { /* noop */ }
        logout();
      }
    },
  });
}

/* --------------------------------------------------------------- *
 * Reset password flow (3 steps)                                    *
 *                                                                  *
 *   1. POST /api/obd/sendOtp/number   { username }                 *
 *      success -> "OTP Sent to your registered Mobile number"      *
 *                                                                  *
 *   2. POST /api/obd/verify/otp       { username, otp }            *
 *      success -> "OTP Verification Successfull" + { userId }      *
 *      (note: the "Successfull" misspelling is the wire format)    *
 *                                                                  *
 *   3. POST /api/obd/user/pwd/reset   { password, userId }         *
 *      success -> "Password Reset Successfully"                    *
 *                                                                  *
 * All endpoints return 200 with `{ message: ... }` on errors, so   *
 * the mutationFn must inspect the message and throw manually.      *
 * --------------------------------------------------------------- */

interface OtpResponse {
  message?: string;
  userId?: string;
}

const SEND_OTP_SUCCESS = 'OTP Sent to your registered Mobile number';
const VERIFY_OTP_SUCCESS = 'OTP Verification Successfull'; // wire-format typo preserved
const PWD_RESET_SUCCESS = 'Password Reset Successfully';

function postPublic<T>(url: string, body: unknown): Promise<T> {
  return api.post<T>(url, body, {
    transformRequest: [
      (data, headers) => {
        if (headers) delete (headers as Record<string, unknown>).Authorization;
        return JSON.stringify(data);
      },
    ],
  });
}

export function useSendOtpMutation() {
  return useMutation({
    mutationFn: async ({ username }: { username: string }) => {
      const res = await postPublic<OtpResponse>('/api/obd/sendOtp/number', { username });
      if (res.message !== SEND_OTP_SUCCESS) {
        throw new Error(res.message ?? 'Could not send OTP');
      }
      return res;
    },
  });
}

export function useVerifyOtpMutation() {
  return useMutation({
    mutationFn: async ({
      username,
      otp,
    }: {
      username: string;
      otp: string;
    }): Promise<{ userId: string }> => {
      const res = await postPublic<OtpResponse>('/api/obd/verify/otp', { username, otp });
      if (res.message !== VERIFY_OTP_SUCCESS || !res.userId) {
        throw new Error(res.message ?? 'OTP verification failed');
      }
      return { userId: res.userId };
    },
  });
}

export function useResetPasswordMutation() {
  return useMutation({
    mutationFn: async ({ userId, password }: { userId: string; password: string }) => {
      const res = await postPublic<{ message?: string }>('/api/obd/user/pwd/reset', {
        password,
        userId,
      });
      if (res.message !== PWD_RESET_SUCCESS) {
        throw new Error(res.message ?? 'Password reset failed');
      }
      return res;
    },
  });
}

/* --------------------------------------------------------------- *
 * Update password via email-link token.                            *
 *   POST /api/obd/update/pwd  { password, token }                  *
 *   success -> "Password Reset Successfully"                       *
 * --------------------------------------------------------------- */

export function useUpdatePasswordMutation() {
  return useMutation({
    mutationFn: async ({ token, password }: { token: string; password: string }) => {
      const res = await postPublic<{ message?: string }>('/api/obd/update/pwd', {
        password,
        token,
      });
      if (res.message !== PWD_RESET_SUCCESS) {
        throw new Error(res.message ?? 'Password update failed');
      }
      return res;
    },
  });
}
